import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { adminService } from '../services/authService';

interface CostRates {
  openaiCostPer1kTokens: number;
  runwareCostPerImage: number;
  astrologyCostPerRequest: number;
  avgTokensPerDream: number;
  avgTokensPerImage: number;
}

interface CostBreakdown {
  period: {
    days: number;
    startDate: string;
    endDate: string;
    tokens: number;
    costs: {
      ai: number;
      runware: number;
      astrology: number;
      total: number;
    };
    modelBreakdown?: Array<{
      model: string;
      _sum: {
        totalTokens: number | null;
        totalCost: number | null;
      };
    }>;
  };
  total: {
    tokens: number;
    costs: {
      ai: number;
      runware: number;
      astrology: number;
      total: number;
    };
  };
  rates: CostRates;
}

interface AnalyticsData {
  userGrowth: Array<{ month: string; users: number }>;
  dreamStats: Array<{ category: string; count: number }>;
  apiUsage: Array<{ date: string; requests: number }>;
  userActivity: Array<{ hour: number; activeUsers: number }>;
  overview: {
    totalUsers: number;
    totalDreams: number;
    totalImages: number;
    activeUsers: number;
  };
  costs: CostBreakdown;
}

const AnalyticsPage: React.FC = () => {
  const [data, setData] = useState<AnalyticsData>({
    userGrowth: [],
    dreamStats: [],
    apiUsage: [],
    userActivity: [],
    overview: {
      totalUsers: 0,
      totalDreams: 0,
      totalImages: 0,
      activeUsers: 0,
    },
    costs: {
      period: {
        days: 30,
        startDate: '',
        endDate: '',
        tokens: 0,
        costs: {
          ai: 0,
          runware: 0,
          astrology: 0,
          total: 0,
        },
        modelBreakdown: [],
      },
      total: {
        tokens: 0,
        costs: {
          ai: 0,
          runware: 0,
          astrology: 0,
          total: 0,
        },
      },
      rates: {
        openaiCostPer1kTokens: 0,
        runwareCostPerImage: 0,
        astrologyCostPerRequest: 0,
        avgTokensPerDream: 0,
        avgTokensPerImage: 0,
      },
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      const [statsData, costsData] = await Promise.all([
        adminService.getStats(),
        adminService.getCosts(30)
      ]);

      // Generate mock chart data based on real stats
      const userGrowth = [
        { month: 'Oca', users: Math.floor(statsData.totalUsers * 0.1) },
        { month: 'Şub', users: Math.floor(statsData.totalUsers * 0.2) },
        { month: 'Mar', users: Math.floor(statsData.totalUsers * 0.3) },
        { month: 'Nis', users: Math.floor(statsData.totalUsers * 0.4) },
        { month: 'May', users: Math.floor(statsData.totalUsers * 0.6) },
        { month: 'Haz', users: statsData.totalUsers },
      ];

      const dreamStats = [
        { category: 'Korku', count: Math.floor(statsData.totalDreams * 0.2) },
        { category: 'Mutluluk', count: Math.floor(statsData.totalDreams * 0.3) },
        { category: 'Aşk', count: Math.floor(statsData.totalDreams * 0.15) },
        { category: 'Başarı', count: Math.floor(statsData.totalDreams * 0.2) },
        { category: 'Aile', count: Math.floor(statsData.totalDreams * 0.15) },
      ];

      const apiUsage = [
        { date: '01/01', requests: Math.floor(statsData.costs.tokens * 0.1) },
        { date: '02/01', requests: Math.floor(statsData.costs.tokens * 0.15) },
        { date: '03/01', requests: Math.floor(statsData.costs.tokens * 0.12) },
        { date: '04/01', requests: Math.floor(statsData.costs.tokens * 0.18) },
        { date: '05/01', requests: Math.floor(statsData.costs.tokens * 0.2) },
        { date: '06/01', requests: Math.floor(statsData.costs.tokens * 0.25) },
      ];

      const userActivity = [
        { hour: 0, activeUsers: Math.floor(statsData.activeUsers * 0.1) },
        { hour: 6, activeUsers: Math.floor(statsData.activeUsers * 0.3) },
        { hour: 12, activeUsers: Math.floor(statsData.activeUsers * 0.8) },
        { hour: 18, activeUsers: statsData.activeUsers },
        { hour: 24, activeUsers: Math.floor(statsData.activeUsers * 0.4) },
      ];

      const totalTokens = costsData.total.tokens || 0;
      const openaiCost = costsData.total.costs?.ai || 0;
      const totalDreams = statsData.totalDreams || 0;
      const totalImages = statsData.totalImages || 0;

      const derivedRates: CostRates = {
        openaiCostPer1kTokens:
          totalTokens > 0 ? openaiCost / (totalTokens / 1000) : 0,
        runwareCostPerImage: costsData.rates?.runwareCostPerImage || 0,
        astrologyCostPerRequest: costsData.rates?.astrologyCostPerRequest || 0,
        avgTokensPerDream: totalDreams > 0 ? Math.round(totalTokens / totalDreams) : 0,
        avgTokensPerImage: totalImages > 0 ? Math.round(totalTokens / totalImages) : 0,
      };

      const enhancedCosts: CostBreakdown = {
        period: {
          ...costsData.period,
          modelBreakdown: costsData.period.modelBreakdown || [],
        },
        total: costsData.total,
        rates: derivedRates,
      };

      setData({
        userGrowth,
        dreamStats,
        apiUsage,
        userActivity,
        overview: {
          totalUsers: statsData.totalUsers || 0,
          totalDreams: statsData.totalDreams || 0,
          totalImages: statsData.totalImages || 0,
          activeUsers: statsData.activeUsers || 0,
        },
        costs: enhancedCosts,
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setLoading(false);
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Analitik</h1>
        <p className="mt-1 text-sm text-gray-500">
          Uygulama performansı ve kullanıcı istatistikleri
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* User Growth Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Kullanıcı Büyümesi
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.userGrowth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="users"
                stroke="#0ea5e9"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Dream Categories */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Rüya Kategorileri
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.dreamStats}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ category, percent }) =>
                  `${category} ${(percent * 100).toFixed(0)}%`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {data.dreamStats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Usage */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            API Kullanımı
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.apiUsage}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="requests" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* User Activity */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Kullanıcı Aktivitesi (Saatlik)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.userActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="activeUsers"
                stroke="#f59e0b"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-2xl font-bold text-primary-600">
            {data.overview.totalDreams.toLocaleString()}
          </div>
          <div className="text-sm text-gray-500">Toplam Rüya</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">
            {data.overview.totalImages.toLocaleString()}
          </div>
          <div className="text-sm text-gray-500">Toplam Görsel</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-2xl font-bold text-yellow-600">{data.costs.total.tokens.toLocaleString()}</div>
          <div className="text-sm text-gray-500">Toplam Token</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-600">${data.costs.total.costs.total.toFixed(4)}</div>
          <div className="text-sm text-gray-500">Toplam Maliyet</div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Son {data.costs.period.days} Gün Maliyetleri
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">AI (OpenAI)</span>
              <span className="text-sm font-medium">
                ${data.costs.period.costs.ai.toFixed(4)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Runware</span>
              <span className="text-sm font-medium">
                ${data.costs.period.costs.runware.toFixed(4)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">FreeAstrology</span>
              <span className="text-sm font-medium">
                ${data.costs.period.costs.astrology.toFixed(4)}
              </span>
            </div>
            <div className="border-t pt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">Toplam</span>
                <span className="text-sm font-bold text-gray-900">${data.costs.period.costs.total.toFixed(4)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Maliyet Oranları
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">OpenAI / 1K Token</span>
              <span className="text-sm font-medium">${data.costs.rates.openaiCostPer1kTokens.toFixed(6)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Runware / Görsel</span>
              <span className="text-sm font-medium">${data.costs.rates.runwareCostPerImage.toFixed(4)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">FreeAstrology / İstek</span>
              <span className="text-sm font-medium">
                ${data.costs.rates.astrologyCostPerRequest.toFixed(4)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Ort. Token / Rüya</span>
              <span className="text-sm font-medium">{data.costs.rates.avgTokensPerDream}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Ort. Token / Görsel</span>
              <span className="text-sm font-medium">{data.costs.rates.avgTokensPerImage}</span>
            </div>
          </div>
        </div>
      </div>

      {data.costs.period.modelBreakdown &&
        data.costs.period.modelBreakdown.length > 0 && (
          <div className="mt-8 bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Model Bazlı Token & Maliyet
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Model
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Token
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Maliyet
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.costs.period.modelBreakdown.map((item) => (
                    <tr key={item.model}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {item.model}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {(item._sum.totalTokens || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        ${(item._sum.totalCost || 0).toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
};

export default AnalyticsPage;
